import React, { useState, useRef, useEffect } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

function SabiasQue({ icono = "💬", texto = "" }) {
	const [visible, setVisible] = useState(false);
	const [showAbove, setShowAbove] = useState(false);
	const containerRef = useRef(null);
	useEffect(() => {
		if (!visible) return;
		const h = (e) => {
			if (containerRef.current && !containerRef.current.contains(e.target))
				setVisible(false);
		};
		document.addEventListener("click", h);
		return () => document.removeEventListener("click", h);
	}, [visible]);
	const toggle = (e) => {
		e.stopPropagation();
		if (!visible && containerRef.current) {
			const rect = containerRef.current.getBoundingClientRect();
			const spaceBelow = window.innerHeight - rect.bottom;
			setShowAbove(spaceBelow < 160 && rect.top > 160);
		}
		setVisible(!visible);
	};
	return (
		<div
			ref={containerRef}
			style={{ position: "relative", display: "inline-block" }}
		>
			<div
				style={{
					width: 32,
					height: 32,
					borderRadius: "50%",
					backgroundColor: "#fff",
					border: "1px solid #ccc",
					boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					cursor: "pointer",
					fontSize: 16,
				}}
				onClick={toggle}
			>
				{icono}
			</div>
			{visible && (
				<div
					style={{
						position: "absolute",
						bottom: showAbove ? "120%" : "auto",
						top: showAbove ? "auto" : "120%",
						left: "50%",
						transform: "translateX(-50%)",
						backgroundColor: "#fffbe6",
						border: "2px solid #f5c400",
						borderRadius: 12,
						padding: "10px 14px",
						fontSize: 12,
						fontFamily: '"Poppins", sans-serif',
						color: "#444",
						width: 250,
						boxShadow: "2px 2px 6px rgba(0, 0, 0, 0.1)",
						zIndex: 10,
						whiteSpace: "normal",
					}}
				>
					{texto}
				</div>
			)}
		</div>
	);
}

const estiloH3 = {
	fontFamily: '"Poppins", sans-serif',
	fontSize: 14,
	fontWeight: 700,
	textTransform: "uppercase",
	margin: 0,
};
const estiloP = {
	fontFamily: '"Poppins", sans-serif',
	fontSize: 13,
	color: "#000",
	margin: 0,
};

// Template72: Día 1 Midtown Manhattan. Contenido desde page.texts y page.images (servidor).
const Template72 = ({
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItems = (html) => {
		if (!html) return [];
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const ps = doc.querySelectorAll("p");
		const items = [];
		ps.forEach((p) => {
			const text = (p.textContent ?? p.innerText ?? "").trim();
			if (!text) return;
			const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
			if (emojiMatch)
				items.push({ icono: emojiMatch[1], texto: emojiMatch[2].trim() });
		});
		return items;
	};

	const title = page?.texts?.[0]?.content;
	const introHtml = page?.texts?.[1]?.content;
	const quintaTitle = page?.texts?.[2]?.content;
	const quintaBodyHtml = page?.texts?.[4]?.content;
	const rockefellerTitle = page?.texts?.[5]?.content;
	const rockefellerBodyHtml = page?.texts?.[6]?.content;
	const momaTitle = page?.texts?.[7]?.content;
	const momaBodyHtml = page?.texts?.[8]?.content;
	const sabiasQueRockefeller = parseSabiasQueItems(page?.texts?.[9]?.content);
	const legoStickerText = page?.texts?.[10]?.content;
	const ctaBoxHtml = page?.texts?.[11]?.content;
	const ctaSabiasQueRaw = page?.texts?.[12]?.content;
	const ctaSabiasQue = (() => {
		if (!ctaSabiasQueRaw || typeof ctaSabiasQueRaw !== "string") return null;
		const trim = ctaSabiasQueRaw.trim();
		const match = trim.match(/^([^\s]+)\s+(.*)/s);
		return match ? { icono: match[1], texto: match[2].trim() } : null;
	})();

	const heroSrc = getImageSrc("times-main");
	const quintaSrc = getImageSrc("quinta");
	const rockefellerSrc = getImageSrc("rockefeller");
	const momaSrc = getImageSrc("moma");
	const legoSrc = getImageSrc("lego");
	const timesStickerSrc = getImageSrc("times-midtown");
	const logoSrc = getCanvasImageUrl("logo");

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "auto",
				backgroundColor: "#f7f7f7",
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
				"& .margin": {
					marginBottom: "8px !important",
				},
				"& a": {
					color: "#007a8d",
					fontWeight: 700,
					textDecoration: "underline",
				},
			}}
		>
			<div
				data-block-id="header"
				style={{
					backgroundColor: headerBackgroundColor || "#59c3d8",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: fontFamily || '"Poppins", sans-serif',
					fontSize: "14px",
					fontWeight: 700,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{title}
				{logoSrc && (
					<img
						src={logoSrc}
						alt="logo"
						style={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 10539,
						}}
					/>
				)}
			</div>

			<div data-block-id="image-0" style={{ position: "relative" }}>
				<img
					src={heroSrc}
					alt="Times"
					style={{ width: "100%", height: 200, objectFit: "cover" }}
				/>
				<h1
					style={{
						fontFamily: '"Poppins", sans-serif',
						fontSize: isSmallScreen ? 28 : 32,
						textAlign: "center",
						position: "absolute",
						bottom: "0",
						margin: 10,
						left: 20,
						color: "#fff",
						textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
					}}
				>
					Midtown Manhattan
				</h1>
				<img
					src={timesStickerSrc}
					alt="Times"
					style={{ width: 120, position: "absolute", bottom: 60, left: 10 }}
				/>
			</div>

			<div
				style={{
					padding: "10px 12px",
					paddingBottom: "5px",
					position: "relative",
				}}
			>
				{introHtml && (
					<div
						data-block-id="text-1"
						style={{
							fontFamily: '"Poppins", sans-serif',
							fontSize: 13,
							color: "#333",
							margin: 0,
							padding: 0,
						}}
						dangerouslySetInnerHTML={{ __html: introHtml }}
					/>
				)}

				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: 10,
						marginTop: 10,
					}}
				>
					{(quintaTitle || quintaBodyHtml || quintaSrc) && (
						<div data-block-id="text-2" style={{ display: "flex", gap: 16 }}>
							{quintaSrc && (
								<img
									src={quintaSrc}
									alt="Quinta Avenida"
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: 12,
									}}
								/>
							)}
							<div style={{ width: "60%" }}>
								{quintaTitle && <h3 style={estiloH3}>{quintaTitle}</h3>}
								{quintaBodyHtml && (
									<div
										style={{ ...estiloP, color: "#444" }}
										dangerouslySetInnerHTML={{ __html: quintaBodyHtml }}
									/>
								)}
							</div>
						</div>
					)}

					{(momaTitle || momaBodyHtml || momaSrc) && (
						<div data-block-id="text-3" style={{ display: "flex", gap: 16, position: "relative" }}>
							<div style={{ width: "100%" }}>
								<p
									style={{
										...estiloP,
										padding: 20,
										backgroundColor: "#e0f7fa",
										borderRadius: 12,
									}}
								>
									{momaSrc && (
										<img
											src={momaSrc}
											alt="Moma"
											style={{
												width: isSmallScreen ? 105 : 115,
												float: "right",
												marginRight: -34,
												marginTop: -25,
											}}
										/>
									)}
									{momaTitle && <h3 style={estiloH3}>{momaTitle}</h3>}
									{momaBodyHtml && (
										<span dangerouslySetInnerHTML={{ __html: momaBodyHtml }} />
									)}
								</p>
							</div>
						</div>
					)}

					{legoStickerText && (
						<div
							style={{
								position: "absolute",
								bottom: isSmallScreen ? 180 : 160,
								left: 4,
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 16px 0px",
								padding: "10px 12px",
								fontSize: 11,
								fontFamily: '"Poppins", sans-serif',
								color: "#444",
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: 120,
								zIndex: 6,
							}}
							dangerouslySetInnerHTML={{ __html: legoStickerText }}
						/>
					)}
					{legoSrc && (
						<img
							src={legoSrc}
							alt="Lego"
							style={{
								width: 100,
								position: "absolute",
								bottom: isSmallScreen ? 90 : 60,
								left: -20,
								zIndex: 5,
							}}
						/>
					)}

					{(rockefellerTitle || rockefellerBodyHtml || rockefellerSrc) && (
						<div
							data-block-id="text-4"
							style={{
								display: "flex",
								gap: 16,
								position: "relative",
								marginTop: -10,
							}}
						>
							{rockefellerSrc && (
								<img
									src={rockefellerSrc}
									alt="Top of the Rock"
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: 12,
									}}
								/>
							)}
							{sabiasQueRockefeller[0] && (
								<div style={{ position: "absolute", top: -10, left: "32%" }}>
									<SabiasQue
										icono={sabiasQueRockefeller[0].icono}
										texto={sabiasQueRockefeller[0].texto}
									/>
								</div>
							)}
							{sabiasQueRockefeller[1] && (
								<div style={{ position: "absolute", top: 32, left: "32%" }}>
									<SabiasQue
										icono={sabiasQueRockefeller[1].icono}
										texto={sabiasQueRockefeller[1].texto}
									/>
								</div>
							)}
							<div style={{ width: "60%" }}>
								{rockefellerTitle && (
									<h3 style={estiloH3}>{rockefellerTitle}</h3>
								)}
								{rockefellerBodyHtml && (
									<div
										style={estiloP}
										dangerouslySetInnerHTML={{ __html: rockefellerBodyHtml }}
									/>
								)}
							</div>
						</div>
					)}

					{(ctaBoxHtml || ctaSabiasQue) && (
						<div
							data-block-id="text-5"
							style={{
								backgroundColor: "#e0f7fa",
								border: "2px solid #59c3d8",
								borderRadius: "12px",
								padding: "8px 16px",
								fontSize: "13px",
								color: "#444",
								marginTop: -4,
								textAlign: "center",
								marginBottom: isSmallScreen ? 10 : 0,
								position: "relative",
							}}
						>
							{ctaBoxHtml && (
								<div
									style={estiloP}
									dangerouslySetInnerHTML={{ __html: ctaBoxHtml }}
								/>
							)}
							{ctaSabiasQue && (
								<div
									style={{
										position: "absolute",
										bottom: isSmallScreen ? 80 : 50,
										right: isSmallScreen ? "60%" : "30%",
									}}
								>
									<SabiasQue
										icono={ctaSabiasQue.icono}
										texto={ctaSabiasQue.texto}
									/>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</Box>
	);
};

export default Template72;
